import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { changePassword, getMe, updateProfile } from "../api/auth";
import { ApiError } from "../api/client";
import { listStories } from "../api/stories";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import { useAuth } from "../hooks/useAuth";

export function ProfilePage() {
	const navigate = useNavigate();
	const auth = useAuth();
	const queryClient = useQueryClient();
	const { t, i18n } = useTranslation();

	const { data: user } = useQuery({ queryKey: ["me"], queryFn: getMe });
	const { data: stories } = useQuery({
		queryKey: ["stories"],
		queryFn: listStories,
	});

	// Name edit state
	const [editingName, setEditingName] = useState(false);
	const [nameInput, setNameInput] = useState("");
	const [nameSaving, setNameSaving] = useState(false);

	// Password change state
	const [showPassword, setShowPassword] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [passwordSuccess, setPasswordSuccess] = useState(false);
	const [passwordSaving, setPasswordSaving] = useState(false);
	const nameInputRef = useRef<HTMLInputElement>(null);
	const currentPasswordRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (passwordSuccess) {
			const timer = setTimeout(() => setPasswordSuccess(false), 3000);
			return () => clearTimeout(timer);
		}
	}, [passwordSuccess]);

	useEffect(() => {
		if (editingName) {
			nameInputRef.current?.focus();
		}
	}, [editingName]);

	useEffect(() => {
		if (showPassword) {
			currentPasswordRef.current?.focus();
		}
	}, [showPassword]);

	function startEditName() {
		setNameInput(user?.name ?? "");
		setEditingName(true);
	}

	function cancelEditName() {
		setEditingName(false);
	}

	async function saveName() {
		if (!nameInput.trim()) return;
		setNameSaving(true);
		try {
			await updateProfile({ name: nameInput.trim() });
			auth.updateName(nameInput.trim());
			queryClient.invalidateQueries({ queryKey: ["me"] });
			setEditingName(false);
		} finally {
			setNameSaving(false);
		}
	}

	function closePasswordPanel() {
		setShowPassword(false);
		setCurrentPassword("");
		setNewPassword("");
		setPasswordError("");
	}

	async function savePassword() {
		setPasswordError("");
		setPasswordSaving(true);
		try {
			await changePassword(currentPassword, newPassword);
			closePasswordPanel();
			setPasswordSuccess(true);
		} catch (err) {
			if (err instanceof ApiError && err.status === 400) {
				setPasswordError(t("profile.errorPasswordWrong"));
			} else {
				setPasswordError(t("profile.errorGeneric"));
			}
		} finally {
			setPasswordSaving(false);
		}
	}

	const memberSince = user?.created_at
		? new Date(user.created_at).toLocaleDateString(i18n.language, {
				month: "long",
				year: "numeric",
			})
		: null;

	const storyCount = stories != null ? stories.length : null;

	return (
		<div className="min-h-screen px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<button
				type="button"
				onClick={() => navigate("/stories")}
				className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors mb-8 inline-block"
			>
				{t("profile.back")}
			</button>

			<h1 className="font-display text-5xl font-bold text-ink leading-tight mb-2">
				{t("profile.headline")}
			</h1>

			<InkDivider className="mb-8" />

			{/* Email */}
			<div className="mb-6">
				<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-1">
					{t("profile.email")}
				</p>
				<p className="font-body text-ink text-lg">{user?.email ?? "—"}</p>
			</div>

			{/* Name */}
			<div className="mb-8">
				<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-1">
					{t("profile.name")}
				</p>
				{editingName ? (
					<div className="flex items-center gap-3">
						<input
							ref={nameInputRef}
							type="text"
							value={nameInput}
							onChange={(e) => setNameInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") saveName();
								if (e.key === "Escape") cancelEditName();
							}}
							className="font-body text-ink text-lg bg-transparent border-b border-ink/30 focus:border-ink outline-none flex-1 py-0.5"
						/>
						<button
							type="button"
							onClick={saveName}
							disabled={nameSaving || !nameInput.trim()}
							className="font-ui text-xs uppercase tracking-widest text-ink hover:text-ink/60 transition-colors disabled:opacity-40"
						>
							{nameSaving ? t("profile.saving") : t("profile.save")}
						</button>
						<button
							type="button"
							onClick={cancelEditName}
							className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/60 transition-colors"
						>
							{t("profile.cancel")}
						</button>
					</div>
				) : (
					<div className="flex items-center gap-3">
						<p className="font-body text-ink text-lg">{user?.name ?? "—"}</p>
						<button
							type="button"
							onClick={startEditName}
							className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
						>
							{t("profile.edit")}
						</button>
					</div>
				)}
			</div>

			{/* Password */}
			{passwordSuccess && (
				<p className="font-ui text-xs uppercase tracking-widest text-ink/60 mb-4">
					{t("profile.passwordUpdated")}
				</p>
			)}

			{!showPassword ? (
				<button
					type="button"
					onClick={() => setShowPassword(true)}
					className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
				>
					{t("profile.changePassword")}
				</button>
			) : (
				<div className="space-y-4">
					<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40">
						{t("profile.changePassword")}
					</p>
					<div>
						<label
							htmlFor="current-password"
							className="font-ui text-[10px] uppercase tracking-widest text-ink/40 block mb-1"
						>
							{t("profile.currentPassword")}
						</label>
						<input
							id="current-password"
							ref={currentPasswordRef}
							type="password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							className="font-body text-ink bg-transparent border-b border-ink/30 focus:border-ink outline-none w-full py-0.5"
						/>
					</div>
					<div>
						<label
							htmlFor="new-password"
							className="font-ui text-[10px] uppercase tracking-widest text-ink/40 block mb-1"
						>
							{t("profile.newPassword")}
						</label>
						<input
							id="new-password"
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") savePassword();
								if (e.key === "Escape") closePasswordPanel();
							}}
							className="font-body text-ink bg-transparent border-b border-ink/30 focus:border-ink outline-none w-full py-0.5"
						/>
					</div>
					{passwordError && (
						<p className="font-ui text-xs text-coral">{passwordError}</p>
					)}
					<div className="flex items-center gap-4">
						<button
							type="button"
							onClick={savePassword}
							disabled={passwordSaving || !currentPassword || !newPassword}
							className="font-ui text-xs uppercase tracking-widest text-ink hover:text-ink/60 transition-colors disabled:opacity-40"
						>
							{passwordSaving ? t("profile.saving") : t("profile.save")}
						</button>
						<button
							type="button"
							onClick={closePasswordPanel}
							className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/60 transition-colors"
						>
							{t("profile.cancel")}
						</button>
					</div>
				</div>
			)}

			<InkDivider className="mb-8 mt-4" />

			{/* Stats */}
			<p className="font-ui text-xs uppercase tracking-widest text-ink/40 mb-8">
				{memberSince ? t("profile.memberSince", { date: memberSince }) : "—"}
				{" · "}
				{storyCount != null
					? t("profile.storiesCount", { count: storyCount })
					: "—"}
			</p>

			<button
				type="button"
				onClick={auth.logout}
				className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors"
			>
				{t("profile.signOut")}
			</button>
		</div>
	);
}
